import { msg } from "@lingui/core/macro";
import { z } from "zod";

export const unknownPostalCodeSchema = z.string().brand("unknownPostalCode");
export type UnknownPostalCode = z.infer<typeof unknownPostalCodeSchema>;

export const internalPostalCodeSchema = z.object({
  SE: z
    .string()
    .regex(/^\d{5}$/)
    .brand("internalSEPostalCode"),
  CH: z
    .string()
    .regex(/^\d{4}$/)
    .brand("internalCHPostalCode"),
  LV: z
    .string()
    .regex(/^\d{4}$/)
    .brand("internalLVPostalCode"),
});
export type InternalFormatPostalCode = z.infer<typeof internalPostalCodeSchema>;
export const isInternalFormatPostalCode = (
  postalCode: unknown,
  countryCode: "SE" | "CH" | "LV",
): postalCode is InternalFormatPostalCode[keyof InternalFormatPostalCode] =>
  internalPostalCodeSchema.shape[countryCode].safeParse(postalCode).success;

const errorMessage = (expectedLength: number) =>
  msg`Ange postnumret med ${expectedLength} siffror`;
export const conventionalPostalCodePattern = {
  SE: { value: /^\d{3}\s?\d{2}$/, message: errorMessage(5) },
  CH: { value: /^\d{4}$/, message: errorMessage(4) },
  LV: { value: /^(LV-)?\d{4}$/, message: errorMessage(4) },
};

export const conventionalPostalCodeSchema = z.object({
  SE: z
    .string()
    .regex(conventionalPostalCodePattern.SE.value)
    .brand("conventionalSEPostalCode"),
  CH: z
    .string()
    .regex(conventionalPostalCodePattern.CH.value)
    .brand("conventionalCHPostalCode"),
  LV: z
    .string()
    .regex(conventionalPostalCodePattern.LV.value)
    .brand("conventionalLVPostalCode"),
});
export type ConventionalPostalCode = z.infer<
  typeof conventionalPostalCodeSchema
>;

const mapCompatiblePostalCodeSchema = z.object({
  SE: z
    .string()
    .regex(/^\d{3}\s\d{2}$/)
    .brand("mapCompatibleSEPostalCode"),
  CH: z
    .string()
    .regex(/^\d{4}$/)
    .brand("mapCompatibleCHPostalCode"),
  LV: z
    .string()
    .regex(/^\d{4}$/)
    .brand("mapCompatibleLVPostalCode"),
});

const beautifiedPostalCodeSchema = z.object({
  SE: z
    .string()
    .regex(/^\d{3}\s\d{2}$/)
    .brand("beautifiedSEPostalCode"),
  CH: z
    .string()
    .regex(/^\d{4}$/)
    .brand("beautifiedCHPostalCode"),
  LV: z
    .string()
    .regex(/^LV-\d{4}$/)
    .brand("beautifiedLVPostalCode"),
});

// Display postal code per local conventions
// Unknown format accepted, and displayed as-is if it doesn't match expectations
export const beautifyPostalCode = (
  postalCode:
    | InternalFormatPostalCode[keyof InternalFormatPostalCode]
    | UnknownPostalCode,
  countryCode: "SE" | "CH" | "LV",
) => {
  if (!isInternalFormatPostalCode(postalCode, countryCode)) {
    return postalCode;
  }

  switch (countryCode) {
    case "SE":
      return beautifiedPostalCodeSchema.shape.SE.parse(
        `${postalCode.substring(0, 3)} ${postalCode.substring(3)}`,
      );

    case "CH":
      return beautifiedPostalCodeSchema.shape.CH.parse(postalCode);

    case "LV":
      return beautifiedPostalCodeSchema.shape.LV.parse(`LV-${postalCode}`);

    default:
      throw countryCode satisfies never;
  }
};

// Requires exact input type, because the "internal" format is the one we base logic and backend requests on
export const internalizePostalCode = (
  postalCode: ConventionalPostalCode[keyof ConventionalPostalCode],
  countryCode: "SE" | "CH" | "LV",
) => {
  switch (countryCode) {
    case "SE":
      return internalPostalCodeSchema.shape.SE.parse(
        postalCode.replaceAll(" ", ""),
      );

    case "CH":
      return internalPostalCodeSchema.shape.CH.parse(postalCode);

    case "LV":
      return internalPostalCodeSchema.shape.LV.parse(
        postalCode.startsWith("LV-") ? postalCode.substring(3) : postalCode,
      );

    default:
      throw countryCode satisfies never;
  }
};

// Mapbox has a preference on which format postal codes should have for best address retrieval
// Accepts Unknown type, which can cause strange look-ups
// Probably better than not doing any look-up for Unknown
export const makePostalCodeMapFriendly = (
  postalCode:
    | InternalFormatPostalCode[keyof InternalFormatPostalCode]
    | UnknownPostalCode,
  countryCode: "SE" | "CH" | "LV",
) => {
  if (!isInternalFormatPostalCode(postalCode, countryCode)) {
    return postalCode;
  }

  switch (countryCode) {
    case "SE":
      return mapCompatiblePostalCodeSchema.shape.SE.parse(
        `${postalCode.substring(0, 3)} ${postalCode.substring(3)}`,
      );

    case "CH":
      return mapCompatiblePostalCodeSchema.shape.CH.parse(postalCode);

    case "LV":
      return mapCompatiblePostalCodeSchema.shape.LV.parse(postalCode);

    default:
      throw countryCode satisfies never;
  }
};
